import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  colors,
  FixedActionBar,
  CustomSectionTitle,
  CustomModal,
  CustomButton,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import useGlobalToast from "@/hooks/useGlobalToast";
import type { AppDispatch, RootState } from "@/store";
import { fetchBudgetCategories } from "@/store/budgetSlice";
import {
  fetchCategoryManagement,
  upsertCategoryManagement,
} from "@/store/categoryManagementSlice";
import {
  fetchGroupOptionManagement,
  upsertGroupOptionManagement,
} from "@/store/groupOptionManagementSlice";
import {
  fetchRelatedLawManagement,
  upsertRelatedLawManagement,
} from "@/store/relatedLawManagementSlice";
import OptionListEditor from "./OptionListEditor";
import BudgetCategorySection from "./BudgetCategorySection";
import PulldownAccordion from "./PulldownAccordion";
import type { PulldownEditorHandle } from "./pulldownSortable";
import { Can } from "@/components/auth/Can";

type MasterKey = "budget" | "group" | "relatedLaw" | "repairCategory";

/**
 * カスタム設定 > カスタムプルダウン タブ。
 * 各種「選択肢マスタ」を、画面ラベルに対応したセクション (予実管理 / 物件情報 /
 * 点検清掃 / 修繕) ごとにセクションタイトル + アコーディオンで管理する
 * (カスタムフォーム「詳細情報」の見せ方を踏襲)。
 * 保存は下部 FixedActionBar の「更新」で全マスタ一括コミット
 * (アコーディオンを閉じても中身はマウント維持されるため未保存の編集は保持される)。
 */
const CustomPulldownTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  // 各エディタの命令的ハンドル (更新時に save() を呼ぶ)
  const budgetRef = useRef<PulldownEditorHandle>(null);
  const groupRef = useRef<PulldownEditorHandle>(null);
  const lawRef = useRef<PulldownEditorHandle>(null);
  const repairRef = useRef<PulldownEditorHandle>(null);

  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteNames, setPendingDeleteNames] = useState<string[]>([]);
  const [deleteBlockMessages, setDeleteBlockMessages] = useState<string[]>([]);
  const [dirtyMap, setDirtyMap] = useState<Record<MasterKey, boolean>>({
    budget: false,
    group: false,
    relatedLaw: false,
    repairCategory: false,
  });
  const [expanded, setExpanded] = useState<Record<MasterKey, boolean>>({
    budget: false,
    group: false,
    relatedLaw: false,
    repairCategory: false,
  });

  const budgetCount = useSelector(
    (s: RootState) => s.budgets.categories.data.length,
  );
  const categoryRecord = useSelector(
    (s: RootState) => s.categoryManagement.record,
  );
  const groupRecord = useSelector(
    (s: RootState) => s.groupOptionManagement.record,
  );
  const relatedLawRecord = useSelector(
    (s: RootState) => s.relatedLawManagement.record,
  );

  const groups = groupRecord?.groups ?? [];
  const relatedLaws = relatedLawRecord?.related_laws ?? [];
  const repairCategories = categoryRecord?.categories ?? [];

  useEffect(() => {
    void dispatch(fetchBudgetCategories());
    void dispatch(fetchCategoryManagement());
    void dispatch(fetchGroupOptionManagement());
    void dispatch(fetchRelatedLawManagement());
  }, [dispatch]);

  const setDirtyFor = (key: MasterKey) => (dirty: boolean) =>
    setDirtyMap((prev) =>
      prev[key] === dirty ? prev : { ...prev, [key]: dirty },
    );
  const toggle = (key: MasterKey) => (next: boolean) =>
    setExpanded((prev) => ({ ...prev, [key]: next }));

  const commitGroups = async (next: string[]): Promise<string | null> => {
    const r = await dispatch(
      upsertGroupOptionManagement({
        existingId: groupRecord?.group_option_management_id ?? null,
        groups: next,
      }),
    );
    return upsertGroupOptionManagement.fulfilled.match(r)
      ? null
      : "グループの保存に失敗しました";
  };
  const commitRelatedLaws = async (next: string[]): Promise<string | null> => {
    const r = await dispatch(
      upsertRelatedLawManagement({
        existingId: relatedLawRecord?.related_law_management_id ?? null,
        related_laws: next,
      }),
    );
    return upsertRelatedLawManagement.fulfilled.match(r)
      ? null
      : "関連法令の保存に失敗しました";
  };
  const commitRepairCategories = async (
    next: string[],
  ): Promise<string | null> => {
    const r = await dispatch(
      upsertCategoryManagement({
        existingId: categoryRecord?.category_management_id ?? null,
        categories: next,
      }),
    );
    return upsertCategoryManagement.fulfilled.match(r)
      ? null
      : "カテゴリの保存に失敗しました";
  };

  const anyDirty = Object.values(dirtyMap).some(Boolean);

  // 「更新」押下: 削除対象があれば注意喚起モーダルを挟む。無ければそのままコミット。
  // 並び替えの即時保存 (更新ボタンとは独立)。配列を upsert し、失敗時のみ通知。
  const handleReorder =
    (commit: (next: string[]) => Promise<string | null>) =>
    async (next: string[]) => {
      const err = await commit(next);
      if (err) {
        showToast({ type: "error", message: "並び替えの保存に失敗しました" });
      }
    };

  const editorRefs = [budgetRef, groupRef, lawRef, repairRef];
  const formatErrors = (errors: string[]) =>
    errors.length === 1
      ? errors[0]
      : `更新できませんでした。以下を確認してください。\n${errors
          .map((error) => `・${error}`)
          .join("\n")}`;

  const requestUpdate = async () => {
    const dels = editorRefs.flatMap((r) => r.current?.pendingDeletions() ?? []);
    if (dels.length > 0) {
      setSubmitting(true);
      const errors: string[] = [];
      try {
        for (const r of editorRefs) {
          if (r.current?.validateDeletions) {
            errors.push(...(await r.current.validateDeletions()));
          }
        }
      } finally {
        setSubmitting(false);
      }
      if (errors.length > 0) {
        setPendingDeleteNames(dels);
        setDeleteBlockMessages(errors);
        setConfirmOpen(true);
        return;
      }
      setPendingDeleteNames(dels);
      setDeleteBlockMessages([]);
      setConfirmOpen(true);
    } else {
      void runUpdate();
    }
  };

  // 全エディタの save() を呼び、結果をまとめて通知
  const runUpdate = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const errors: string[] = [];
    try {
      for (const r of editorRefs) {
        if (r.current) errors.push(...(await r.current.save()));
      }
    } finally {
      setSubmitting(false);
    }
    if (errors.length) {
      showToast({
        type: "error",
        message: formatErrors(errors),
      });
    } else {
      showToast({ type: "success", message: "更新しました" });
    }
  };

  const closeConfirmModal = () => {
    setConfirmOpen(false);
    setDeleteBlockMessages([]);
  };

  const sectionTitleSx = { mt: 2.5, mb: 1 };

  return (
    <CustomFormNavigationGuard hasFormChanged={anyDirty}>
      {() => (
        <Box pb={9} pt={1}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: colors.text.secondary,
              fontSize: { xs: 12, sm: 14 },
              lineHeight: 1.6,
            }}
          >
            各画面のプルダウン (選択肢)
            を管理します。編集後、下部の「更新」で保存します。
          </Typography>

          <Box sx={{ width: { xs: "100%", md: "60%" } }}>
            {/* 予実管理 */}
            <Box sx={{ ...sectionTitleSx, mt: 1 }}>
              <CustomSectionTitle title="予実管理" size="small" />
            </Box>
            <PulldownAccordion
              title="明細項目"
              count={budgetCount}
              expanded={expanded.budget}
              onToggle={toggle("budget")}
            >
              <BudgetCategorySection
                ref={budgetRef}
                disabled={submitting}
                onDirtyChange={setDirtyFor("budget")}
              />
            </PulldownAccordion>

            {/* 物件情報 */}
            <Box sx={sectionTitleSx}>
              <CustomSectionTitle title="物件情報" size="small" />
            </Box>
            <PulldownAccordion
              title="グループ"
              count={groups.length}
              expanded={expanded.group}
              onToggle={toggle("group")}
            >
              <OptionListEditor
                ref={groupRef}
                description="物件をまとめるグループ。物件の作成・編集や予実分析の集計で使います。"
                options={groups}
                disabled={submitting}
                onDirtyChange={setDirtyFor("group")}
                onSave={commitGroups}
                onReorder={handleReorder(commitGroups)}
              />
            </PulldownAccordion>

            {/* 点検/清掃 */}
            <Box sx={sectionTitleSx}>
              <CustomSectionTitle title="点検/清掃" size="small" />
            </Box>
            <PulldownAccordion
              title="関連法令"
              count={relatedLaws.length}
              expanded={expanded.relatedLaw}
              onToggle={toggle("relatedLaw")}
            >
              <OptionListEditor
                ref={lawRef}
                description="管理業務に紐づける関連法令。点検/清掃の業務編集で選択肢になります。"
                options={relatedLaws}
                disabled={submitting}
                onDirtyChange={setDirtyFor("relatedLaw")}
                onSave={commitRelatedLaws}
                onReorder={handleReorder(commitRelatedLaws)}
              />
            </PulldownAccordion>

            {/* 修繕 */}
            <Box sx={sectionTitleSx}>
              <CustomSectionTitle title="修繕" size="small" />
            </Box>
            <PulldownAccordion
              title="カテゴリ"
              count={repairCategories.length}
              expanded={expanded.repairCategory}
              onToggle={toggle("repairCategory")}
            >
              <OptionListEditor
                ref={repairRef}
                description="修繕の分類。修繕の作成・編集で選択肢になります。"
                options={repairCategories}
                disabled={submitting}
                onDirtyChange={setDirtyFor("repairCategory")}
                onSave={commitRepairCategories}
                onReorder={handleReorder(commitRepairCategories)}
              />
            </PulldownAccordion>
          </Box>

          <Can action="master:edit">
            <FixedActionBar
              submitLabel="更新"
              isLoading={submitting}
              disabled={!anyDirty || submitting}
              onSubmit={requestUpdate}
            />
          </Can>

          {/* 削除を含む更新の注意喚起 (アプリ標準の削除確認文言) */}
          <CustomModal
            openModal={confirmOpen}
            handleCloseModal={closeConfirmModal}
            title={
              deleteBlockMessages.length > 0
                ? "削除できません"
                : "削除してよろしいですか？"
            }
            modalType="deleteModal"
            bodyText=""
            hideCloseButton
          >
            <Box>
              {deleteBlockMessages.length > 0 ? (
                <Typography
                  sx={{
                    fontSize: 14,
                    color: colors.text.primary,
                    lineHeight: 1.7,
                  }}
                >
                  {deleteBlockMessages.map((message) => (
                    <React.Fragment key={message}>
                      {message}
                      <br />
                    </React.Fragment>
                  ))}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 14, color: colors.text.secondary }}>
                  次の項目を削除します: {pendingDeleteNames.join("、")}
                  <br />
                  この操作は取り消せません。削除してよろしいですか？
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <CustomButton
                label={deleteBlockMessages.length > 0 ? "閉じる" : "戻る"}
                buttonCategory="cancelModal"
                onClick={closeConfirmModal}
                disabled={submitting}
              />
              {deleteBlockMessages.length === 0 && (
                <Can action="master:delete">
                  <CustomButton
                    label="削除する"
                    buttonCategory="deleteModal"
                    onClick={() => void runUpdate()}
                    isLoading={submitting}
                    disabled={submitting}
                  />
                </Can>
              )}
            </Box>
          </CustomModal>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default CustomPulldownTab;
